import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import location components (lazy-loaded for performance)
const LocationsList = React.lazy(() => import("./LocationsList"));
const NewLocation = React.lazy(() => import("./NewLocation"));
const LocationDetails = React.lazy(() => import("./LocationDetails"));

/**
 * Locations Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (location/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/location/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 *
 * Permission levels:
 * - view: View locations, stats, room occupancy, hierarchy
 * - create: Add new locations
 * - edit: Update locations, generate QR codes, mark cleaned/inspected
 * - delete: Remove locations (admin only)
 */
const locationsModuleConfig: ModuleConfig = {
    // Module Identity
    id: "locations",
    name: "Location Management",
    description: "Manage physical locations, buildings, rooms and areas for shifts and check-ins",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 4,
    icon: "📍",
    color: "#38A169",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/locations",
            component: LocationsList,
            permissionLevel: "view",
        },
        {
            path: "/locations/new",
            component: NewLocation,
            permissionLevel: "create",
        },
        {
            path: "/locations/:id",
            component: LocationDetails,
            permissionLevel: "view",
        },
        {
            path: "/locations/edit/:id",
            component: NewLocation, // Reuse NewLocation component for editing
            permissionLevel: "edit",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Locations",
            path: "/locations",
            icon: "📍",
            permissionLevel: "view",
        },
        {
            label: "Add Location",
            path: "/locations/new",
            icon: "➕",
            permissionLevel: "create",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/location/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // Quick Actions
    quickActions: [
        {
            label: "Add Location",
            path: "/locations/new",
            icon: "➕",
            description: "Register a new physical location"
        },
        {
            label: "View All",
            path: "/locations",
            icon: "📍",
            description: "View all locations"
        }
    ]
};

export default locationsModuleConfig;
